/**
 * BulkVerifyWizard — full-screen modal that walks an owner through
 * every currently-empty registry field on a document, one at a time.
 *
 * Trigger: CTA on `/uni-portal/degree/{id}` (and later, universities
 * — but the initial ship is degree-only per Feb 2026 request).
 *
 * Data flow:
 *   1. Loads /api/uni-portal/registry to know every editable field
 *      + its section, type, help text.
 *   2. Filters to fields where `dotted(doc, spec.path)` is empty.
 *   3. Presents them one at a time with a progress bar, Skip,
 *      Save & Next.
 *   4. Each save POSTs /api/uni-portal/degrees/{id}/field — the
 *      same endpoint the inline RegistryFieldEditor uses, so all
 *      writes hit the shared audit trail.
 *
 * The wizard is deliberately dumb about long-text fields — it
 * skips anything with `max > 500` (those are staff-only narrative
 * sections anyway, but the guard makes it future-proof).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BadgeCheck,
  ArrowRight,
  SkipForward,
  Loader2,
  Sparkles,
} from "lucide-react";


function dotted(doc, path) {
  let cur = doc;
  for (const seg of (path || "").split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = cur[seg];
    if (cur == null) return undefined;
  }
  return cur;
}


export default function BulkVerifyWizard({
  open,
  onOpenChange,
  docType = "degrees",  // "degrees" for now — universities is P1
  docId,
  doc,
  onSaved,              // (data) => void — called on every successful save
}) {
  const [registry, setRegistry] = useState(null);
  const [idx, setIdx] = useState(0);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  // Build the queue of empty, owner-editable, non-long-text fields.
  const queue = useMemo(() => {
    if (!registry || !doc) return [];
    return registry
      .filter((f) => f.collection === docType)
      .filter((f) => f.actor_scope === "university" || f.actor_scope === "both")
      .filter((f) => !(f.type === "text" && (f.max || 0) > 500))
      .filter((f) => {
        const v = dotted(doc, f.path);
        return v === undefined || v === null || v === "";
      });
  }, [registry, doc, docType]);

  const current = queue[idx];

  // Load registry once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/uni-portal/registry");
        if (!cancelled) setRegistry(data?.fields || data || []);
      } catch {
        if (!cancelled) setRegistry([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset step-local state whenever we land on a new field.
  useEffect(() => {
    setValue("");
    setBusy(false);
  }, [idx, open]);

  const closeAll = () => {
    onOpenChange(false);
    setIdx(0);
    setValue("");
  };

  const advance = () => {
    if (idx + 1 >= queue.length) {
      toast.success("All done — every field covered.");
      closeAll();
    } else {
      setIdx(idx + 1);
    }
  };

  const skip = () => {
    advance();
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!current || busy || value === "") return;
    setBusy(true);
    try {
      let v = value;
      if (current.type === "bool") v = value === "true";
      const { data } = await api.post(
        `/uni-portal/${docType}/${docId}/field`,
        {
          section: current.section,
          field_label: current.label,
          new_value: v,
        },
      );
      onSaved?.(data);
      toast.success(`Verified ${current.label}`);
      advance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      setBusy(false);
    }
  };

  const total = queue.length;
  const pct = total ? Math.round(((idx) / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="bulk-verify-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-editorial text-2xl">
            <Sparkles className="h-5 w-5 text-[#4C1D95]" />
            Bulk verify
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Step through every empty registry field one at a time. Skip anything
            you can't answer — every save updates the audit trail immediately.
          </DialogDescription>
        </DialogHeader>

        {registry === null ? (
          <div className="py-8 text-center text-stone-500">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading fields…
          </div>
        ) : total === 0 ? (
          <div
            className="py-8 text-center"
            data-testid="bulk-verify-empty"
          >
            <BadgeCheck className="h-10 w-10 text-emerald-600 mx-auto" />
            <div className="mt-2 font-editorial text-lg text-stone-800">
              Every editable field is already set.
            </div>
            <p className="mt-1 text-sm text-stone-500">
              Nothing to verify right now — thanks for keeping the
              record clean.
            </p>
            <Button className="mt-4" onClick={closeAll} data-testid="bulk-verify-close">
              Close
            </Button>
          </div>
        ) : current ? (
          <div data-testid={`bulk-verify-step-${idx}`}>
            <div
              className="h-1.5 rounded-full bg-stone-100 overflow-hidden"
              aria-label={`Step ${idx + 1} of ${total}`}
            >
              <div
                className="h-full bg-emerald-600 transition-[width] duration-400"
                style={{ width: `${Math.max(3, pct)}%` }}
                data-testid="bulk-verify-progress"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] font-underground-sc tracking-widest text-stone-500">
              <span>
                Field {idx + 1} of {total}
              </span>
              <span
                className="rounded px-1.5 py-0.5 border border-stone-200 bg-stone-50 text-stone-600"
                data-testid="bulk-verify-section"
              >
                {current.section}
              </span>
            </div>

            <form onSubmit={save} className="mt-5 space-y-3">
              <label
                htmlFor="bulk-verify-input"
                className="block text-[13px] font-medium text-stone-900"
              >
                {current.label}
              </label>
              {current.help && (
                <p className="text-[11px] text-stone-500 leading-snug">
                  {current.help}
                </p>
              )}

              {current.type === "bool" ? (
                <select
                  id="bulk-verify-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full rounded-md border border-stone-300 bg-white p-2 text-sm"
                  data-testid="bulk-verify-input"
                >
                  <option value="">Pick…</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : current.type === "date" ? (
                <Input
                  id="bulk-verify-input"
                  type="date"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="bulk-verify-input"
                />
              ) : (
                <Input
                  id="bulk-verify-input"
                  type={current.type === "int" || current.type === "float" || current.type === "usd" || current.type === "gbp" ? "number" : "text"}
                  step={current.type === "float" ? "any" : undefined}
                  min={current.min ?? undefined}
                  max={current.max && current.type !== "text" ? current.max : undefined}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={current.type === "usd" || current.type === "gbp" ? "e.g. 9250" : ""}
                  maxLength={current.type === "text" ? (current.max || undefined) : undefined}
                  data-testid="bulk-verify-input"
                />
              )}

              <div className="flex items-center justify-between gap-3 pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={skip}
                  disabled={busy}
                  data-testid="bulk-verify-skip"
                  className="text-stone-500 hover:text-stone-900"
                >
                  <SkipForward className="h-3.5 w-3.5 mr-1" />
                  Skip
                </Button>
                <Button
                  type="submit"
                  disabled={busy || value === ""}
                  data-testid="bulk-verify-save"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {busy ? "Saving…" : "Save & Next"}
                  {!busy && <ArrowRight className="h-3.5 w-3.5 ml-1" />}
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
